import { AdmissionSaveError } from './admission-save-error'
import { apiClient } from './api-client'
import { bookingStudentFlow } from '@/src/config/flows/booking-student.flow'
import { transformFormDataToPayload } from '@/src/lib/flow-engine/payloadTransform'
import type {
  AdmissionLoadResponse,
  AdmissionRecoverResponse,
  AdmissionSavePayload,
  AdmissionSaveResponse,
  AdmissionVerifyOldResponse,
  CodeOption,
  StructureOption
} from '@/src/types/api.types'

// ─── Admissions (POST) ────────────────────────────────────────────────

export function saveStudentStep(payload: AdmissionSavePayload) {
  return apiClient.post<AdmissionSaveResponse>('/admissions/save', payload)
}

/**
 * Convenience wrapper used by the wizard final step.
 * Accepts the legacy shape { journey, formData, submittedAt } and
 * transforms it to the API format, then forwards to /admissions/save with finalStep: true.
 */
export async function submitStudentBooking(payload: {
  journey: string
  formData: Record<string, unknown>
  submittedAt: string
  currentStep?: number
  finalStep?: boolean
  draftId?: string | null
  lang?: string
}) {
  const currentStep = payload.currentStep ?? bookingStudentFlow.steps.length
  const isFinalStep = payload.finalStep ?? true
  const transformedPayload = transformFormDataToPayload(
    payload.formData,
    bookingStudentFlow,
    currentStep,
    isFinalStep,
    payload.draftId ?? null,
    payload.lang
  )

  const res = await apiClient.post<AdmissionSaveResponse>('/admissions/save', transformedPayload)

  if (res.error) {
    throw new AdmissionSaveError({
      returnCode: res.returnCode ?? 'UNKNOWN_ERROR',
      message: res.message ?? 'Errore nel salvataggio della domanda.',
      error: true,
      errorMessages: res.errorMessages ?? [],
      body: null
    })
  }

  return {
    success: res.returnCode === 'OK',
    message: res.returnMessage ?? '',
    data: res
  }
}

export function loadStudentDraft(email: string, password: string) {
  return apiClient.post<AdmissionLoadResponse>('/admissions/load', {
    username: email,
    password
  })
}

export function recoverStudentDraft(email: string, lang: string) {
  return apiClient.post<AdmissionRecoverResponse>(
    '/admissions/recover',
    { username: email, lingua: lang },
    { language: lang }
  )
}

export function verifyOldStudentDraft(email: string) {
  return apiClient.post<AdmissionVerifyOldResponse>('/admissions/verifyold', {
    username: email
  })
}

// ─── Codes / Lookups (GET) ─────────────────────────────────────────────

export function getStudentCities(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/residenzecamplus/pnrrcitiesby/studenti/all', { lang })
}

export function getStudentStructures(cityId: string, lang: string) {
  return apiClient.get<StructureOption[]>(
    `/codes/residenzecamplus/structuresby/${cityId}/studenti`,
    { lang }
  )
}

export function getStudentRooms(sedeId: string, lang: string) {
  return apiClient.get<CodeOption[]>(`/codes/residenzecamplus/rooms/${sedeId}`, { lang })
}

export function getStudentDurations(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/duratastudenti', { lang })
}

export function getStudentMotivations(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/motivoscelta', { lang })
}

export function getStudentMotivationsPnrr(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/motivosceltapnrr', { lang })
}
